/**
 * Atlas PM · P8.1 任务详情 · 「存为模板」弹窗。
 *
 * 位置：任务详情抽屉底部操作区「存为模板」按钮唤起。
 * 内容：模板名称（默认填任务名）+ 说明 + 范围（个人 / 公共）。
 *
 * ⚠️ 公共模板仅超管 / 持 template:manage 权限者可建：无权限时该选项置灰并说明原因。
 * ⚠️ 结构由后端从任务提取，本弹窗只提交名称 / 说明 / 范围。
 * ⚠️ 保存中禁用按钮（P7.9 的 submitted 守卫同款），避免重复提交。
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import { ApiError } from '../../api/client';
import { useAuthStore } from '../../stores/auth';
import {
  taskTemplateApi,
  TEMPLATE_QUERY_KEY,
  type TemplateScope,
} from '../task-form/taskTemplates';

export interface SaveTemplateDialogProps {
  taskId: string;
  taskName: string;
  open: boolean;
  onClose(): void;
}

export default function SaveTemplateDialog({
  taskId,
  taskName,
  open,
  onClose,
}: SaveTemplateDialogProps) {
  /** ⭐ 公共模板管理权限（超管恒 true）；无权限者只能存个人模板 */
  const canManagePublic = useAuthStore((s) => s.hasPermission('template:manage'));
  const queryClient = useQueryClient();

  const [name, setName] = useState(taskName);
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<TemplateScope>('PERSONAL');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // 每次打开重置表单（避免跨任务串值）
  useEffect(() => {
    if (!open) return;
    setName(taskName);
    setDescription('');
    setScope('PERSONAL');
    setSubmitting(false);
    setSaved(false);
    setError('');
  }, [open, taskName]);

  const onSave = async () => {
    if (submitting || saved) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError('请填写模板名称');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await taskTemplateApi.fromTask(taskId, {
        name: trimmed,
        description: description.trim() || undefined,
        scope,
      });
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: TEMPLATE_QUERY_KEY });
      // 提示「模板已保存」后自动关闭
      window.setTimeout(onClose, 900);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存失败');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      size="sm"
      title="存为模板"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={submitting || saved}
            data-testid="save-template-submit"
          >
            {saved ? '已保存' : submitting ? '保存中…' : '保存'}
          </Button>
        </>
      }
    >
      <div className="pm-delete-explain">
        把当前任务的「指标 + 要素」结构存为模板，下次新建任务可直接套用。
        模板只保存结构，不含完成度、负责人、日期与所属项目。
      </div>

      <label className="tt-field">
        <span className="tt-field__label">模板名称 *</span>
        <input
          className="td-input"
          data-testid="save-template-name"
          maxLength={60}
          placeholder="例如：设备安装标准任务"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <label className="tt-field">
        <span className="tt-field__label">说明</span>
        <textarea
          className="td-input"
          data-testid="save-template-description"
          rows={2}
          maxLength={200}
          placeholder="选填，简要说明模板用途"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className="tt-field">
        <span className="tt-field__label">范围</span>
        <div className="tt-scope">
          <label className="tt-scope__opt">
            <input
              type="radio"
              name="template-scope"
              data-testid="save-template-scope-personal"
              checked={scope === 'PERSONAL'}
              onChange={() => setScope('PERSONAL')}
            />
            个人模板（仅自己可见）
          </label>
          <label
            className={`tt-scope__opt${canManagePublic ? '' : ' is-disabled'}`}
            title={canManagePublic ? undefined : '需要模板管理权限，请联系管理员'}
          >
            <input
              type="radio"
              name="template-scope"
              data-testid="save-template-scope-public"
              disabled={!canManagePublic}
              checked={scope === 'PUBLIC'}
              onChange={() => setScope('PUBLIC')}
            />
            公共模板（全员可用）
          </label>
          {!canManagePublic && (
            <div className="tt-scope__hint" data-testid="save-template-public-hint">
              公共模板需要「模板管理」权限，当前仅可保存为个人模板。
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="pm-modal__err" data-testid="save-template-error">
          ⚠ {error}
        </div>
      )}
      {saved && (
        <div className="pm-modal__ok" data-testid="save-template-ok">
          模板已保存
        </div>
      )}
    </Modal>
  );
}
